import { DownOutlined } from "@ant-design/icons";
import { Button, Col, Form, Input, Modal, Row, Select } from "antd";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import {
  DateIcon,
  DateTimePicker,
  Icons,
  PhTag,
  TimeIcon,
} from "../../../../../components";
import {
  getDisableDatesBeforeStart,
  getDisablePastDates,
  getDisablePastTimes,
  getDisableTimesBeforeStart,
  validateEndDateTime,
  validateStartTime,
} from "../../../../../utils/dateUtils";

interface ExecutionMonitoringModalProps {
  open: boolean;
  onClose: Function;
  form: any;
}

const ExecutionMonitoringModal = ({
  open,
  onClose,
  form,
}: ExecutionMonitoringModalProps) => {
  const fromDate = Form.useWatch("fromdate", form);
  const startTime = Form.useWatch("starttime", form);
  const toDate = Form.useWatch("todate", form);
  const endTime = Form.useWatch("endtime", form);

  const [hasErrors, setHasErrors] = useState<boolean>(true);
  const [submitAttempted, setSubmitAttempted] = useState<boolean>(false);

  const handleNextButton = async () => {
    setSubmitAttempted(true);
    try {
      await form.validateFields();
      onClose(true);
    } catch (e) {
      // Validation errors will be shown by Form.Item
    }
  };

  useEffect(() => {
    // Validate relevant fields and set hasErrors accordingly
    const fieldsToValidate = [
      "fromdate",
      "todate",
      "starttime",
      "endtime",
      "frequency",
      "failedNotifyFailCount",
      "email",
    ];

    form
      .validateFields(fieldsToValidate, { validateOnly: true })
      .then(() => setHasErrors(false))
      .catch(() => setHasErrors(true));
  }, [fromDate, startTime, toDate, endTime, form]);

  return (
    <>
      <Modal
        className="max-user-modal fp-modal fp-modal-primary modal-margins"
        width={600}
        open={open}
        title="Synthetic monitoring"
        closable
        maskClosable={false}
        onCancel={() => onClose()}
        footer={[]}
      >
        <Form
          layout="vertical"
          wrapperCol={{ span: 12 }}
          className="required-icon"
          form={form}
        >
          <Row>
            <Col className="form-col">
              <DateIcon
                style={{
                  textAlign: "center",
                  marginTop: "40px",
                  marginRight: "20px",
                }}
              />
              <Form.Item
                name="fromdate"
                label="From"
                rules={[{ required: true, message: "Date is required!" }]}
              >
                <DateTimePicker
                  picker="date"
                  suffixIcon={<DownOutlined />}
                  format="dddd, MMMM D"
                  className="fb_picker_input"
                  disabledDate={getDisablePastDates()}
                />
              </Form.Item>
            </Col>
            <Col className="form-col">
              <Icons
                style={{
                  textAlign: "center",
                  marginTop: "40px",
                  marginRight: "20px",
                }}
              />
              <Form.Item
                name="todate"
                label="To"
                dependencies={["fromdate", "starttime"]}
                rules={[
                  { required: true, message: "Date is required!" },
                  {
                    validator: (_, value) => {
                      if (!value || !toDate) return Promise.resolve();
                      if (toDate.valueOf() < fromDate.valueOf()) {
                        return Promise.reject(
                          new Error("End date cannot be before start date"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <DateTimePicker
                  picker="date"
                  suffixIcon={<DownOutlined />}
                  format="dddd, MMMM D"
                  className="fb_picker_input"
                  disabledDate={getDisableDatesBeforeStart(fromDate)}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col className="form-col">
              <TimeIcon
                style={{
                  textAlign: "center",
                  marginTop: "40px",
                  marginRight: "20px",
                }}
              />
              <Form.Item
                name="starttime"
                label="Start Time"
                dependencies={["fromdate"]}
                rules={[
                  { required: true, message: "Start time is required!" },
                  {
                    validator: (_, value) => {
                      const result = validateStartTime(
                        value,
                        fromDate,
                        false,
                        5,
                      );
                      if (result === true) return Promise.resolve();
                      return Promise.reject(new Error(result as string));
                    },
                  },
                ]}
              >
                <DateTimePicker
                  picker="time"
                  suffixIcon={<DownOutlined />}
                  format="h:mm a"
                  className="fb_picker_input"
                  placeholder="Select a start time"
                  disabledTime={getDisablePastTimes(fromDate, 5)}
                  showNow
                  onChange={(time) => {
                    if (time) {
                      const now = DateTime.now();
                      const diffMs = Math.abs(time.toMillis() - now.toMillis());

                      // If selected time is within 30 seconds of now, assume "Now" was clicked
                      if (diffMs < 30000) {
                        const adjustedTime = time.plus({ minutes: 2 });
                        form.setFieldsValue({ starttime: adjustedTime });
                      }
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col className="form-col">
              <TimeIcon
                style={{
                  textAlign: "center",
                  marginTop: "40px",
                  marginRight: "20px",
                }}
              />
              <Form.Item
                name="endtime"
                label="End Time"
                dependencies={["fromdate", "starttime", "todate"]}
                rules={[
                  { required: true, message: "End time is required!" },
                  {
                    validator: (_, value) => {
                      const result = validateEndDateTime(
                        toDate,
                        value,
                        fromDate,
                        startTime,
                        false,
                      );
                      if (result === true) return Promise.resolve();
                      return Promise.reject(new Error(result as string));
                    },
                  },
                ]}
              >
                <DateTimePicker
                  picker="time"
                  suffixIcon={<DownOutlined />}
                  format="h:mm a"
                  className="fb_picker_input"
                  placeholder="Select a end time"
                  disabledTime={getDisableTimesBeforeStart(
                    fromDate,
                    startTime,
                    toDate,
                    false,
                  )}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <PhTag
              style={{
                textAlign: "center",
                marginTop: "40px",
                marginRight: "20px",
              }}
            />
            <Form.Item
              name="frequency"
              label="Repeat"
              rules={[{ required: true }]}
            >
              <Select
                className="fb_select-max-width selection-menu"
                style={{ width: 490 }}
                options={[
                  {
                    value: "5 minutes",
                    label: "Every 5 minutes",
                  },
                  {
                    value: "15 minutes",
                    label: "Every 15 minutes",
                  },
                  {
                    value: "30 minutes",
                    label: "Every 30 minutes",
                  },
                  {
                    value: "60 minutes",
                    label: "Every 60 minutes",
                  },
                  {
                    value: "2 hours",
                    label: "Every 2 hours",
                  },
                  {
                    value: "4 hours",
                    label: "Every 4 hours",
                  },
                  {
                    value: "6 hours",
                    label: "Every 6 hours",
                  },
                  {
                    value: "8 hours",
                    label: "Every 8 hours",
                  },
                  {
                    value: "10 hours",
                    label: "Every 10 hours",
                  },
                  {
                    value: "12 hours",
                    label: "Every 12 hours",
                  },
                  {
                    value: "24 hours",
                    label: "Every 24 hours",
                  },
                ]}
                placeholder="Select a frequency"
              />
            </Form.Item>
          </Row>
          <Row>
            <PhTag
              style={{
                textAlign: "center",
                marginTop: "40px",
                marginRight: "20px",
              }}
            />
            <Form.Item
              name="failedNotifyFailCount"
              label="Failed to notify when concurrent failed count reach"
              rules={[{ required: true }]}
            >
              <Input
                type="number"
                style={{ width: 490 }}
                className="fb_picker_input"
              />
            </Form.Item>
          </Row>
          <Row>
            <PhTag
              style={{
                textAlign: "center",
                marginTop: "40px",
                marginRight: "20px",
              }}
            />
            <Form.Item
              name="email"
              label="Fail alert notifier"
              rules={[
                {
                  type: "email",
                  required: true,
                  message: "Enter correct email",
                },
              ]}
            >
              <Input
                style={{ width: 490 }}
                placeholder="Enter an email to notify execution fails"
                className="fb_picker_input"
              />
            </Form.Item>
          </Row>
          <Row>
            {submitAttempted && hasErrors && (
              <div style={{ color: "red", marginBottom: 12, width: "100%" }}>
                Please correct the errors above before proceeding.
              </div>
            )}
            <Button
              className="fp_btn fp_btn-tertiary"
              onClick={handleNextButton}
            >
              Next
            </Button>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default ExecutionMonitoringModal;
